/** VoicePro by XenonLabs.ai **/
import { motion, useReducedMotion } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  Shield,
  Loader2,
  HelpCircle,
  Sparkles,
  ArrowRight,
  MessageSquare,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { SEOHead } from "@/components/landing/SEOHead";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";

const CONTACT_EMAIL = "connect@xenonlabs.ai";

const contactFormSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  company: z.string().optional(),
  phone: z.string().optional(),
  message: z.string().min(10, "Message must be at least 10 characters"),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

interface ContactInfoItemProps {
  icon: typeof Mail;
  label: string;
  value: string;
  href?: string;
  testId: string;
}

const ContactInfoItem = ({ icon: Icon, label, value, href, testId }: ContactInfoItemProps) => (
  <motion.div
    className="flex items-start gap-4 group p-4 rounded-2xl hover:bg-muted/40 transition-colors duration-300"
    whileHover={{ x: 4 }}
    data-testid={testId}
  >
    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/20 group-hover:shadow-amber-500/40 group-hover:scale-105 transition-all duration-300">
      <Icon className="h-6 w-6 text-white" />
    </div>
    <div>
      <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{label}</p>
      {href ? (
        <a
          href={href}
          className="text-base font-bold hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
          data-testid={`${testId}-link`}
        >
          {value}
        </a>
      ) : (
        <p className="text-base font-bold">{value}</p>
      )}
    </div>
  </motion.div>
);

const faqItems = [
  {
    id: "faq-1",
    question: "What is the typical response time for support inquiries?",
    answer: "Our support team typically responds within 24 hours during business days. For urgent matters, we offer priority support for Enterprise customers with response times under 2 hours."
  },
  {
    id: "faq-2",
    question: "Can I schedule a demo of the AI voice agents?",
    answer: "Absolutely! Fill out the contact form with your requirements, and our team will reach out to schedule a personalized demo. You can also start with our free trial to experience the platform firsthand."
  },
  {
    id: "faq-3",
    question: "Do you offer custom integrations for enterprise clients?",
    answer: "Yes, we provide custom integration solutions for enterprise clients. Our team can work with your existing infrastructure to ensure seamless integration with your CRM, telephony systems, and other business tools."
  },
  {
    id: "faq-4",
    question: "What payment methods do you accept?",
    answer: "We accept all major credit cards (Visa, MasterCard, American Express), as well as ACH transfers and wire transfers for annual enterprise agreements. All payments are securely processed through Stripe."
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.4, 0.25, 1],
    },
  },
};

export default function Contact() {
  const shouldReduceMotion = useReducedMotion();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      phone: "",
      message: "",
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to send message");
      }

      form.reset();

      toast({
        title: "Message sent successfully!",
        description: result.message || "We'll get back to you within 24 hours.",
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Please try again later.";
      toast({
        title: "Failed to send message",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Contact Us | VoicePro"
        description="Get in touch with VoicePro. We're here to help you transform your call operations with AI voice agents. Reach out for demos, support, or partnership inquiries."
        keywords={["contact", "support", "AI voice agents", "demo", "VoicePro"]}
      />

      <Navbar />

      {/* Hero Section */}
      <section
        className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden"
        data-testid="section-hero"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-amber-50/60 via-transparent to-transparent dark:from-amber-950/20 dark:via-transparent dark:to-transparent" />
        <div className="absolute top-1/3 left-1/4 w-72 h-72 bg-amber-200/20 dark:bg-amber-800/10 rounded-full filter blur-[100px]" />
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-orange-200/20 dark:bg-orange-800/10 rounded-full filter blur-[100px]" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={shouldReduceMotion ? {} : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center space-y-6 max-w-3xl mx-auto"
          >
            <motion.div
              initial={shouldReduceMotion ? {} : { opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/40 dark:to-orange-900/30 text-amber-700 dark:text-amber-300 text-sm font-semibold"
              data-testid="badge-contact"
            >
              <Sparkles className="h-4 w-4" />
              We&apos;d love to hear from you
            </motion.div>

            <h1
              className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight"
              data-testid="heading-contact"
            >
              Contact{" "}
              <span className="bg-gradient-to-r from-amber-600 to-orange-600 dark:from-amber-400 dark:to-orange-400 bg-clip-text text-transparent">
                Us
              </span>
            </h1>

            <p
              className="text-xl md:text-2xl text-muted-foreground leading-relaxed"
              data-testid="text-contact-subheading"
            >
              Have questions about our AI voice agents? Want a personalized demo?
              Our team is here to help you transform your call operations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form + Info Section */}
      <section
        className="py-12 md:py-20 relative"
        data-testid="section-contact-form"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={shouldReduceMotion ? {} : containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-start"
          >
            {/* Form (3/5) */}
            <motion.div variants={shouldReduceMotion ? {} : itemVariants} className="lg:col-span-3 order-2 lg:order-1">
              <Card className="p-6 sm:p-8 md:p-10 rounded-3xl shadow-2xl shadow-amber-500/5 border border-amber-200/30 dark:border-amber-800/20" data-testid="card-contact-form">
                <div className="space-y-1.5 mb-8">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    <h2 className="text-xl font-bold" data-testid="heading-form">
                      Send us a message
                    </h2>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Fill out the form below and we&apos;ll get back to you shortly.
                  </p>
                </div>

                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-5"
                    data-testid="form-contact"
                  >
                    <div className="grid sm:grid-cols-2 gap-5">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-semibold">Name *</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Your full name"
                                className="h-12 rounded-xl bg-muted/40 border-muted-foreground/15 focus:border-amber-500 focus:ring-amber-500/20 transition-colors"
                                data-testid="input-name"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-semibold">Email *</FormLabel>
                            <FormControl>
                              <Input
                                type="email"
                                placeholder="you@company.com"
                                className="h-12 rounded-xl bg-muted/40 border-muted-foreground/15 focus:border-amber-500 focus:ring-amber-500/20 transition-colors"
                                data-testid="input-email"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <FormField
                        control={form.control}
                        name="company"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-semibold">Company</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Your company name"
                                className="h-12 rounded-xl bg-muted/40 border-muted-foreground/15 focus:border-amber-500 focus:ring-amber-500/20 transition-colors"
                                data-testid="input-company"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-semibold">Phone</FormLabel>
                            <FormControl>
                              <Input
                                type="tel"
                                placeholder="+1 (555) 123-4567"
                                className="h-12 rounded-xl bg-muted/40 border-muted-foreground/15 focus:border-amber-500 focus:ring-amber-500/20 transition-colors"
                                data-testid="input-phone"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-semibold">Message *</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Tell us about your needs..."
                              className="min-h-[140px] resize-none rounded-xl bg-muted/40 border-muted-foreground/15 focus:border-amber-500 focus:ring-amber-500/20 transition-colors"
                              data-testid="input-message"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full h-13 text-base font-bold rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white border-0 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300"
                      disabled={isSubmitting}
                      data-testid="button-submit-contact"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-5 w-5" />
                          Send Message
                        </>
                      )}
                    </Button>

                    <p
                      className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1.5 pt-1"
                      data-testid="text-privacy-notice"
                    >
                      <Shield className="h-3.5 w-3.5" />
                      Your data is secure. We never share your information.
                    </p>
                  </form>
                </Form>
              </Card>
            </motion.div>

            {/* Info (2/5) */}
            <motion.div
              variants={shouldReduceMotion ? {} : itemVariants}
              className="lg:col-span-2 space-y-8 order-1 lg:order-2"
            >
              <div className="space-y-2">
                <h2 className="text-2xl font-bold" data-testid="heading-contact-info">
                  Contact Information
                </h2>
                <p className="text-muted-foreground">
                  Reach out to us directly through any of these channels.
                </p>
              </div>

              <div className="space-y-2">
                <ContactInfoItem
                  icon={Mail}
                  label="Email"
                  value={CONTACT_EMAIL}
                  href={`mailto:${CONTACT_EMAIL}`}
                  testId="contact-email"
                />

                <ContactInfoItem
                  icon={Phone}
                  label="Phone"
                  value="+1 (555) 123-4567"
                  href="tel:+15551234567"
                  testId="contact-phone"
                />

                <ContactInfoItem
                  icon={MapPin}
                  label="Address"
                  value="123 AI Boulevard, San Francisco, CA 94105"
                  testId="contact-address"
                />

                <ContactInfoItem
                  icon={Clock}
                  label="Office Hours"
                  value="Monday - Friday, 9:00 AM - 6:00 PM PST"
                  testId="contact-hours"
                />
              </div>

              {/* Map Card */}
              <Card
                className="p-5 rounded-2xl overflow-hidden border border-amber-200/30 dark:border-amber-800/20"
                data-testid="card-map-placeholder"
              >
                <div className="aspect-[16/10] bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/20 rounded-xl flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 opacity-15">
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#8882_1px,transparent_1px),linear-gradient(to_bottom,#8882_1px,transparent_1px)] bg-[size:20px_20px]" />
                  </div>
                  <div className="text-center z-10 space-y-3">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mx-auto shadow-lg shadow-amber-500/30">
                      <MapPin className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-sm">VoicePro HQ</p>
                      <p className="text-xs text-muted-foreground">San Francisco, California</p>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section
        className="py-16 md:py-24 relative"
        data-testid="section-faq"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-muted/30 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={shouldReduceMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center space-y-4 mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/40 dark:to-orange-900/30 text-amber-700 dark:text-amber-300 text-sm font-semibold">
              <HelpCircle className="h-4 w-4" />
              Frequently Asked Questions
            </div>
            <h2 className="text-3xl md:text-4xl font-bold" data-testid="heading-faq">
              Common Questions
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Find quick answers to frequently asked questions about our services.
            </p>
          </motion.div>

          <motion.div
            initial={shouldReduceMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="rounded-2xl overflow-hidden border border-amber-200/30 dark:border-amber-800/20 shadow-lg">
              <Accordion type="single" collapsible className="w-full">
                {faqItems.map((item, index) => (
                  <AccordionItem
                    key={item.id}
                    value={item.id}
                    className={index === faqItems.length - 1 ? "border-b-0" : ""}
                  >
                    <AccordionTrigger
                      className="px-6 py-5 text-left hover:no-underline hover:bg-muted/40 transition-colors"
                      data-testid={`faq-trigger-${index + 1}`}
                    >
                      <span className="font-semibold text-[15px]">{item.question}</span>
                    </AccordionTrigger>
                    <AccordionContent
                      className="px-6 pb-5 text-muted-foreground leading-relaxed"
                      data-testid={`faq-content-${index + 1}`}
                    >
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Card>
          </motion.div>

          <motion.div
            initial={shouldReduceMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-12"
          >
            <p className="text-muted-foreground mb-4" data-testid="text-more-questions">
              Still have questions? We&apos;re here to help.
            </p>
            <Button
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white border-0 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 rounded-xl font-bold transition-all duration-300"
              onClick={() => {
                const formSection = document.getElementById("contact-form");
                if (formSection) {
                  formSection.scrollIntoView({ behavior: "smooth" });
                } else {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
              data-testid="button-contact-cta"
            >
              <Mail className="h-5 w-5" />
              Contact Support
              <ArrowRight className="h-4 w-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
